
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { TokenSource } from 'livekit-client';
import { SessionProvider, useSession } from '@livekit/components-react';
import type { AppConfig } from '@/app-config';
import { useAuth } from '@/hooks/useAuth';
// import { useLangfuseConfig } from '@/hooks/useLangfuseConfig'; // Not needed - using prompt_config_id instead
import { useUserName } from '@/hooks/useUserName';
// import type { LangfusePromptConfig } from '@/types'; // Not needed - using interview mode

interface ConnectionContextType {
  isConnectionActive: boolean;
  connect: () => void;
  startDisconnectTransition: () => void;
  startReconnectTransition: () => boolean;
  onDisconnectTransitionComplete: () => void;
}

const ConnectionContext = createContext<ConnectionContextType>({
  isConnectionActive: false,
  connect: () => { },
  startDisconnectTransition: () => { },
  startReconnectTransition: () => false,
  onDisconnectTransitionComplete: () => { },
});

export function useConnection() {
  const ctx = useContext(ConnectionContext);
  if (!ctx) {
    throw new Error('useConnection must be used within a ConnectionProvider');
  }
  return ctx;
}

interface ConnectionProviderProps {
  appConfig: AppConfig;
  children: React.ReactNode;
  /** Optional design ID for traceability in agent logs */
  designId?: string;
  // designLangfuseConfig?: LangfusePromptConfig; // Not needed - using prompt_config_id instead
}

const MAX_AUTO_RETRIES = 1;

export function ConnectionProvider({
  appConfig,
  children,
  designId,
}: ConnectionProviderProps) {
  const [isConnectionActive, setIsConnectionActive] = useState(false);
  const [pendingReconnect, setPendingReconnect] = useState(false);
  const [, setRetryCount] = useState(0);
  const { idToken, refreshIdToken, user } = useAuth();
  // const { config: globalLangfuseConfig, loading: langfuseLoading } = useLangfuseConfig(); // Not needed - using prompt_config_id
  const { userName } = useUserName();
  const { agentName, sandboxId } = appConfig;

  // Interview mode: agent fetches prompt config from decoa-langfuse Firestore DB via prompt_config_id
  // const langfuseConfig = ...; // Removed - no longer sending langfuse_prompt_name

  const connectionEndpoint = useMemo(() => {
    return typeof import.meta.env.VITE_CONN_DETAILS_ENDPOINT === 'string'
      ? import.meta.env.VITE_CONN_DETAILS_ENDPOINT
      : '/api/connection-details';
  }, []);

  // No loading gate needed - not waiting for Langfuse config fetch
  // const shouldWaitForConfig = !designLangfuseConfig && langfuseLoading;

  const tokenSource = useMemo(() => {
    return TokenSource.custom(async () => {
      const url = connectionEndpoint.startsWith('http')
        ? new URL(connectionEndpoint)
        : new URL(connectionEndpoint, window.location.origin);

      const token = idToken ?? (await refreshIdToken());
      if (!token) {
        throw new Error('Missing authentication token');
      }

      // Build participant attributes - interview mode uses prompt_config_id
      const participantAttributes: Record<string, string> = {};

      // Add Firebase Auth UID for real user tracking
      if (user?.uid) {
        participantAttributes.user_id = user.uid;
      }

      // Add design_id for traceability in agent logs
      if (designId) {
        participantAttributes.design_id = designId;
      }

      // Add user_name if provided
      if (userName) {
        participantAttributes.user_name = userName;
      }

      // Interview mode: agent fetches prompt from PROMPT_CONFIGS["interview"] in firebase_config.py
      // Uses decoa-langfuse Firestore DB; no diary session, no AI analysis
      participantAttributes.prompt_config_id = 'interview';

      // Removed: langfuse_prompt_name, langfuse_prompt_label, langfuse_prompt_version, prompt_variables
      // Agent fetches all prompt config from Firestore langfuse-prompt/1 document directly

      const requestBody = {
        room_config: agentName
          ? {
            agents: [{ agent_name: agentName }],
          }
          : undefined,
        participant_attributes: Object.keys(participantAttributes).length > 0
          ? participantAttributes
          : undefined,
      };

      console.log('=== Connection Request ===');
      console.log('Agent Name:', agentName);
      console.log('Mode: interview (prompt_config_id sent to agent)');
      console.log('Request Body:', JSON.stringify(requestBody, null, 2));

      try {
        const res = await fetch(url.toString(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Sandbox-Id': sandboxId ?? '',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(requestBody),
        });

        if (!res.ok) {
          const message = await res.text();
          throw new Error(message || 'Failed to fetch connection details');
        }

        return await res.json();
      } catch (error) {
        console.error('Error fetching connection details:', error);
        throw new Error('Error fetching connection details!');
      }
    });
  }, [agentName, sandboxId, connectionEndpoint, idToken, refreshIdToken, userName, designId, user]);

  const session = useSession(tokenSource, agentName ? { agentName } : undefined);

  const { start: startSession, end: endSession } = session;

  const startConnection = useCallback(
    (resetRetryCount: boolean) => {
      if (resetRetryCount) {
        setRetryCount(0);
      }
      setPendingReconnect(false);
      setIsConnectionActive(true);
      startSession();
    },
    [startSession]
  );

  const connect = useCallback(() => {
    startConnection(true);
  }, [startConnection]);

  const startDisconnectTransition = useCallback(() => {
    setPendingReconnect(false);
    setIsConnectionActive(false);
  }, []);

  const startReconnectTransition = useCallback(() => {
    let scheduled = false;
    setIsConnectionActive(false);
    setRetryCount((count) => {
      if (count >= MAX_AUTO_RETRIES) {
        setPendingReconnect(false);
        scheduled = false;
        return count;
      }
      setPendingReconnect(true);
      scheduled = true;
      return count + 1;
    });
    return scheduled;
  }, []);

  const onDisconnectTransitionComplete = useCallback(() => {
    endSession();
    if (pendingReconnect) {
      startConnection(false);
    }
  }, [endSession, pendingReconnect, startConnection]);

  const value = useMemo(() => {
    return {
      isConnectionActive,
      connect,
      startDisconnectTransition,
      startReconnectTransition,
      onDisconnectTransitionComplete,
    };
  }, [
    connect,
    isConnectionActive,
    onDisconnectTransitionComplete,
    startDisconnectTransition,
    startReconnectTransition,
  ]);

  return (
    <SessionProvider session={session}>
      <ConnectionContext.Provider value={value}>{children}</ConnectionContext.Provider>
    </SessionProvider>
  );
}
